import { Dispatch, SetStateAction, useState } from 'react'
import { Permissions } from '../../../../types/user-types'
import SummaryTable from '../../wells/summary-table'
import DetailsTabEdit from './details-tab-edit'
import DetailsTabView from './details-tab-view'
import { ERROR_MESSAGES } from '../../errors/messages'
import { useWellId } from '../../../hooks/use-well-id'
import { ColumnHeader, TableAction } from '@msaf/core-react'

interface LithologySummaryTableProps {
  fields: Record<string, string | number | boolean>[] | null
  setFields: React.Dispatch<React.SetStateAction<Record<string, string | number | boolean>[] | null>>
  isEditMode?: boolean
  setWellCanAddStratum?: Dispatch<SetStateAction<boolean | undefined>>
}

export default function LithologySummaryTable({
  fields,
  setFields,
  setWellCanAddStratum,
  isEditMode = false,
}: LithologySummaryTableProps) {
  const wellId = useWellId()
  const [rowId, setRowId] = useState<number | null>(null)

  const onModalClose = () => {
    setRowId(null)
  }

  const rowClickAction: TableAction = {
    label: 'View',
    type: 'transitionAction',
    args: [{ constant: `wells/${wellId}/view/lithology/stratum` }, { elementKey: 'id' }],
  }

  const actions: Array<TableAction> = [
    {
      label: 'Delete',
      type: 'deleteAction',
      args: [
        {
          elementKey: 'id',
        },
      ],
      actionPermissions: [Permissions.DELETE_WELL_DATA],
    },
  ]

  const columnHeaders: Array<ColumnHeader> = [
    {
      elementKey: 'id',
      columnHeading: 'Id',
      viewColumn: 'id',
      type: 'hidden',
    },
    {
      elementKey: 'stratumTop',
      columnHeading: 'Top level (m)',
      viewColumn: 'stratumTop',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'stratumBottom',
      columnHeading: 'Bottom level (m)',
      viewColumn: 'stratumBottom',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'stratumThickness',
      columnHeading: 'Stratum thickness (m)',
      viewColumn: 'stratumThickness',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'coalescedStratumDescriptionElided',
      columnHeading: 'Description',
      viewColumn: 'coalescedStratumDescriptionElided',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'actions',
      columnHeading: 'Actions',
      viewColumn: 'actions',
      type: 'actions-no-data',
      actions,
    },
  ]

  const deleteAction = (id: number) => {
    setRowId(id)
  }

  return (
    <SummaryTable
      fields={fields}
      setFields={setFields}
      columnHeaders={columnHeaders}
      defaultSortBy={{
        orderColumn: 'stratumBottom',
        orderDirection: 'asc',
      }}
      rowClickAction={rowClickAction}
      requestUrl={`wells/${wellId}/stratum`}
      tableActionHandlers={{
        deleteAction,
      }}
      confirmDeleteModalProps={{
        isShowModal: !!rowId,
        requestUrl: `wells/${wellId}/stratum/${rowId}/edit`,
        heading: 'Delete stratum',
        bodyText: 'This will delete the stratum record. Are you sure you wish to proceed?',
        successMessage: 'Stratum deleted',
        errorMessage: ERROR_MESSAGES.DELETE_FAILED,
        onModalClose,
      }}
      noResultsComponent={
        isEditMode === true ? <DetailsTabEdit setWellCanAddStratum={setWellCanAddStratum} /> : <DetailsTabView />
      }
      resultNameSingular='stratum'
      resultNamePlural='strata'
    />
  )
}
